export type EstadoComunicacion = 'BORRADOR' | 'PENDIENTE_FIRMA' | 'ENVIADA' | 'PROGRAMADA';

export interface FirmaAutomaticaStatus {
  disponible: boolean;
}

export interface RemesaConfig {
  permiteDestinatariosExternos: boolean;
  permitePostal: boolean;
  permitePostalInternacional: boolean;
  permiteEnvioSoloApp?: boolean;
  permiteNotifica?: boolean;
  algunModoEnvioActivo?: boolean;
}

/* Canal de entrega de la remesa. */
export type ModoEnvioRemesa = 'APP' | 'NOTIFICA' | 'NOTIFICA_POSTAL';

export function modoEnvioFromRemesa(
  c?: { modoEnvio?: ModoEnvioRemesa; entregaPostal?: boolean } | null
): ModoEnvioRemesa {
  if (c?.modoEnvio) {
    return c.modoEnvio;
  }
  return c?.entregaPostal ? 'NOTIFICA_POSTAL' : 'NOTIFICA';
}

export interface DomicilioPostal {
  tipoDomicilio?: number;
  tipoVia?: string;
  nombreVia?: string;
  numero?: string;
  codigoPostal?: string;
  municipio?: string;
  provincia?: string;
  poblacion?: string;
  pais?: string;
  linea1?: string;
  linea2?: string;
}

export function domicilioPostalVacio(): DomicilioPostal {
  return {
    tipoDomicilio: 1,
    tipoVia: '',
    nombreVia: '',
    numero: '',
    codigoPostal: '',
    municipio: '',
    provincia: '',
    poblacion: '',
    pais: 'ES',
    linea1: '',
    linea2: ''
  };
}

export interface TipoEnvio {
  id: number;
  nombre: string;
}

export interface DestinatarioMin {
  id?: number;
  nombre: string;
  apellido1: string;
  apellido2?: string;
  email: string;
  numero_documento?: string;
  telefono?: string;
  externo?: boolean;
  domicilio?: DomicilioPostal;
}

export function etiquetaDestinatario(d: DestinatarioMin | null | undefined): string {
  if (!d) return '';
  const nombre = `${d.nombre || ''} ${d.apellido1 || ''} ${d.apellido2 || ''}`.trim();
  return nombre || d.numero_documento || d.email || '';
}

export function inicialesDestinatario(d: DestinatarioMin | null | undefined): string {
  if (!d) return '';
  const a = (d.nombre || '').trim().charAt(0);
  const b = (d.apellido1 || '').trim().charAt(0);
  const s = (a + b).toUpperCase();
  return s || (d.numero_documento || '').slice(0, 2).toUpperCase();
}

/** Fila de Excel / payload de destinatario externo (sin usuario de la aplicación). */
export interface DestinatarioExternoPayload {
  usuarioId?: number;
  nif?: string;
  nombre?: string;
  apellidos?: string;
  email?: string;
  telefono?: string;
  domicilio?: DomicilioPostal;
  campos?: Record<string, string>;
}

export interface ProcedimientoRef {
  id: number;
  sia: number;
  nombre: string;
  codigo?: string;
}

export interface ComunicacionResumen {
  id: number;
  concepto: string;
  descripcion?: string;
  tipoEnvio: TipoEnvio;
  estado: EstadoComunicacion;
  fechaCreacion?: string | number[];
  fechaEnvio?: string | number[];
  fechaProgramacion?: string | number[];
  destinatariosCount: number;
  destinatariosPreview: DestinatarioMin[];
  procedimiento?: ProcedimientoRef;
  plantilla?: { id: number; nombre: string };
  documentoModo?: 'ADJUNTO' | 'PLANTILLA' | 'NINGUNO';
  /** Si true, se firma con Autofirma el documento de cada destinatario antes de enviar. Solo notificaciones. */
  firmarAntesEnviar?: boolean;
  /** Canal de entrega: APP | NOTIFICA | NOTIFICA_POSTAL. */
  modoEnvio?: ModoEnvioRemesa;
  /** @deprecated Use modoEnvio === 'NOTIFICA_POSTAL'. */
  entregaPostal?: boolean;
  creadoPor?: DestinatarioMin;
  firmadoPor?: DestinatarioMin;
  enviadoPor?: DestinatarioMin;
  fechaSolicitudFirma?: string | number[];
  fechaFirma?: string | number[];
  /** Documento adjunto común de la remesa (modo ADJUNTO). */
  documento?: DocumentoRef;
}

/** Referencia descargable a un documento del gestor documental. */
export interface DocumentoRef {
  idGestor: string;
  nombre?: string;
  mime?: string;
}

/** Destinatario del detalle, con su envío y los documentos descargables. */
export interface DestinatarioDetalle extends DestinatarioMin {
  envioId?: number;
  estadoEnvio?: string;
  estadoEnvioLabel?: string;
  identificador?: string;
  fechaFirma?: string | number[];
  /** Documento entregado al destinatario (personalizado o adjunto común). */
  documento?: DocumentoRef;
  /** Justificante de aceptación/rechazo firmado por el ciudadano. */
  justificante?: DocumentoRef;
}

export interface ComunicacionDetalle extends ComunicacionResumen {
  destinatarios: DestinatarioDetalle[];
}

export interface ComunicacionStats {
  total: number;
  enviadas: number;
  borradores: number;
  pendientesFirma: number;
  programadas: number;
}

export interface ComunicacionListResponse {
  data: ComunicacionResumen[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Documento adjunto opcional de la remesa (modo manual). Si no se envía, el
 * back genera un documento por destinatario desde plantilla y lo firma con el
 * sello EMVS.
 */
export interface DocumentoAdjunto {
  /** Contenido del fichero en base64 (sin el prefijo data:). */
  contenido: string;
  mime: string;
  nombre: string;
}

export interface ComunicacionPayload {
  id?: number;
  concepto: string;
  descripcion?: string;
  tipoEnvio: number; // id del TipoEnvioNotifica (1 = Comunicación, 2 = Notificación)
  destinatarios: Array<number | DestinatarioExternoPayload>;
  /** SIA del procedimiento. Obligatorio para tipoEnvio = Notificación. */
  procedimiento?: number;
  /** Canal de entrega: APP | NOTIFICA | NOTIFICA_POSTAL. */
  modoEnvio?: ModoEnvioRemesa;
  /** @deprecated Use modoEnvio === 'NOTIFICA_POSTAL'. */
  entregaPostal?: boolean;
  /** Documento adjunto común; si se omite, se genera uno por destinatario. */
  documento?: DocumentoAdjunto;
  /** Plantilla HTML para generar el documento. Null = plantilla por defecto. */
  plantillaId?: number | null;
  /** Modo de documento: 'ADJUNTO' | 'PLANTILLA' | 'NINGUNO'. */
  documentoModo?: 'ADJUNTO' | 'PLANTILLA' | 'NINGUNO';
  /** Se persiste en borrador; si el envío falla, se reintenta al reabrir. */
  firmarAntesEnviar?: boolean;
}

export interface DocumentoParaFirma {
  envioId: number;
  nombre?: string;
  contenido: string;
}

export interface DocumentosFirmaResponse {
  documentos: DocumentoParaFirma[];
}

/** Convención de ids del back: id=1 → Comunicación, id=2 → Notificación. */
export const TIPO_ENVIO_COMUNICACION_ID = 1;
export const TIPO_ENVIO_NOTIFICACION_ID = 2;

/** Etiqueta UI según tipología (comunicación / notificación). */
export function etiquetaTipoEnvio(
  tipo: number | TipoEnvio | undefined | null,
  opts: { capital?: boolean } = {}
): string {
  const id = typeof tipo === 'number' ? tipo : tipo?.id;
  const capital = opts.capital !== false;
  if (id === TIPO_ENVIO_NOTIFICACION_ID) {
    return capital ? 'Notificación' : 'notificación';
  }
  return capital ? 'Comunicación' : 'comunicación';
}
